import { supabase } from '@/lib/supabase/client';
import type { Database } from '@/lib/supabase/types';

export type FeedFilters = {
  topic?: string;
  industry?: string;
  location?: string;
};

type PostRow = Database['public']['Tables']['posts']['Row'];
type ReactionRow = Database['public']['Tables']['reactions']['Row'];
type CommentRow = Database['public']['Tables']['comments']['Row'];

export type Post = PostRow;
export type FeedPost = Post & { feed_rank: number };
export type Reaction = ReactionRow;
export type Comment = CommentRow;
export type ReactionKind = Database['public']['Enums']['reaction_kind'];
export type PostVisibility = Database['public']['Enums']['post_visibility'];

export type CreatePostInput = {
  author_id: string;
  content: string;
  topic: string;
  industry: string;
  location?: string | null;
  visibility?: PostVisibility;
  media_urls?: string[];
  link_preview?: Record<string, unknown> | null;
  group_id?: string | null;
};

export type CommentWithAuthor = CommentRow & {
  author: { id: string; display_name: string; handle: string; avatar_url: string | null };
};

export type PostWithMeta = PostRow & {
  author: { id: string; display_name: string; handle: string; avatar_url: string | null };
  reaction_counts: Record<ReactionKind, number>;
  my_reaction: ReactionKind | null;
  comment_count: number;
};

export async function listFeed(
  viewerProfileId: string,
  before: string = new Date().toISOString(),
  beforeRank = 1,
  limit = 20,
  filters?: FeedFilters,
): Promise<FeedPost[]> {
  const { data, error } = await supabase
    .rpc('list_feed', {
      p_viewer_profile_id: viewerProfileId,
      p_before: before,
      p_limit: limit,
      p_topic: filters?.topic ?? null,
      p_industry: filters?.industry ?? null,
      p_location: filters?.location ?? null,
      p_before_rank: beforeRank,
    })
    .throwOnError();
  if (error) throw error;
  // Cast: types.ts reflects the old setof-posts signature until `pnpm supabase:types` is re-run.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (data ?? []) as any as FeedPost[];
}

const MAX_MEDIA_SIZE = 10 * 1024 * 1024;

export async function uploadPostMedia(files: File[]): Promise<string[]> {
  // The storage RLS policy checks auth.uid(), not profile.id (profiles have a
  // separate auth_user_id FK). Fetch the auth UID from the session directly.
  const { data: { session } } = await supabase.auth.getSession();
  const authUserId = session?.user.id;
  if (!authUserId) throw new Error('Not authenticated');

  const urls: string[] = [];
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    if (file.size > MAX_MEDIA_SIZE) throw new Error(`${file.name} exceeds 10 MB`);
    const ext = file.name.split('.').pop() ?? 'jpg';
    const path = `${authUserId}/${Date.now()}-${i}.${ext}`;
    const { error } = await supabase.storage.from('post-media').upload(path, file);
    if (error) throw error;
    const { data } = supabase.storage.from('post-media').getPublicUrl(path);
    urls.push(data.publicUrl);
  }
  return urls;
}

export async function getPost(postId: string): Promise<Post | null> {
  const { data, error } = await supabase
    .from('posts')
    .select('id, author_id, content, topic, industry, location, visibility, media_urls, link_preview, group_id, created_at, updated_at')
    .eq('id', postId)
    .maybeSingle()
    .throwOnError();
  if (error) throw error;
  return data;
}

export async function createPost(input: CreatePostInput): Promise<Post> {
  const { data, error } = await supabase
    .from('posts')
    .insert({
      author_id: input.author_id,
      content: input.content,
      topic: input.topic,
      industry: input.industry,
      location: input.location ?? null,
      visibility: input.visibility ?? 'public',
      media_urls: input.media_urls ?? [],
      link_preview: (input.link_preview as Database['public']['Tables']['posts']['Insert']['link_preview']) ?? null,
      group_id: input.group_id ?? null,
    })
    .select('id, author_id, content, topic, industry, location, visibility, media_urls, link_preview, group_id, created_at, updated_at')
    .single()
    .throwOnError();
  if (error) throw error;
  return data!;
}

export async function listPostsByAuthor(
  authorId: string,
  before: string = new Date().toISOString(),
  limit = 20,
): Promise<Post[]> {
  const { data, error } = await supabase
    .from('posts')
    .select('id, author_id, content, topic, industry, location, visibility, media_urls, link_preview, group_id, created_at, updated_at')
    .eq('author_id', authorId)
    .lt('created_at', before)
    .order('created_at', { ascending: false })
    .limit(limit)
    .throwOnError();
  if (error) throw error;
  return data ?? [];
}

export async function listGroupPosts(
  groupId: string,
  before: string = new Date().toISOString(),
  limit = 20,
): Promise<Post[]> {
  const { data, error } = await supabase
    .from('posts')
    .select('id, author_id, content, topic, industry, location, visibility, media_urls, link_preview, group_id, created_at, updated_at')
    .eq('group_id', groupId)
    .lt('created_at', before)
    .order('created_at', { ascending: false })
    .limit(limit)
    .throwOnError();
  if (error) throw error;
  return data ?? [];
}

export async function deletePost(postId: string): Promise<void> {
  const { error } = await supabase.from('posts').delete().eq('id', postId).throwOnError();
  if (error) throw error;
}

export async function listComments(postId: string): Promise<CommentRow[]> {
  const { data, error } = await supabase
    .from('comments')
    .select('id, post_id, author_id, parent_comment_id, content, created_at, updated_at')
    .eq('post_id', postId)
    .order('created_at', { ascending: true })
    .throwOnError();
  if (error) throw error;
  return data ?? [];
}

export async function createComment(
  postId: string,
  authorId: string,
  content: string,
  parentCommentId?: string,
): Promise<CommentRow> {
  const { data, error } = await supabase
    .from('comments')
    .insert({
      post_id: postId,
      author_id: authorId,
      content,
      parent_comment_id: parentCommentId ?? null,
    })
    .select('id, post_id, author_id, parent_comment_id, content, created_at, updated_at')
    .single()
    .throwOnError();
  if (error) throw error;
  return data!;
}

export async function deleteComment(commentId: string): Promise<void> {
  const { error } = await supabase.from('comments').delete().eq('id', commentId).throwOnError();
  if (error) throw error;
}

export async function listReactions(postId: string): Promise<ReactionRow[]> {
  const { data, error } = await supabase
    .from('reactions')
    .select('id, post_id, author_id, kind, created_at')
    .eq('post_id', postId)
    .throwOnError();
  if (error) throw error;
  return data ?? [];
}

export async function upsertReaction(
  postId: string,
  authorId: string,
  kind: ReactionKind,
): Promise<ReactionRow> {
  const { data, error } = await supabase
    .from('reactions')
    .upsert(
      { post_id: postId, author_id: authorId, kind },
      { onConflict: 'post_id,author_id' },
    )
    .select('id, post_id, author_id, kind, created_at')
    .single()
    .throwOnError();
  if (error) throw error;
  return data!;
}

export async function deleteReaction(postId: string, authorId: string): Promise<void> {
  const { error } = await supabase
    .from('reactions')
    .delete()
    .eq('post_id', postId)
    .eq('author_id', authorId)
    .throwOnError();
  if (error) throw error;
}

export type FlagReason = 'spam' | 'harassment' | 'misinformation' | 'other';

export async function flagPost(
  postId: string,
  flaggedBy: string,
  reason: FlagReason,
  note?: string,
): Promise<void> {
  await supabase
    .from('post_flags')
    .insert({ post_id: postId, flagged_by: flaggedBy, reason, note: note ?? null })
    .throwOnError();
}
